import React, { useEffect, useState, useContext } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useTenant } from "../Context/TenantContext";
import axios from "axios";
import API_BASE_URL from "../Config/config";
import { useSubscription } from "../Context/SubscriptionContext";
import useSkillGenieSession from "../hooks/useSkillGenieSession";
import { supportedLanguages } from "../utils/languageSupport";
import { ArrowLeft, Clock, LayoutGrid, Home, GraduationCap, Target, ChevronDown } from "lucide-react";
import { SparklesIcon } from "@heroicons/react/24/outline";

import { updateLearningConsistency } from '../utils/consistency';
import { UserContext } from './UserContext';

const QuestionPage = () => {
  const { technology, level } = useParams();
  const navigate = useNavigate();
  const { user: userFromContext } = useContext(UserContext);
  const userId = userFromContext?.uid || userFromContext?.id || localStorage.getItem('userId');

  const { tenant } = useTenant();

 const [questions, setQuestions] = useState([]);
 const [currentIndex, setCurrentIndex] = useState(0);
 const [answers, setAnswers] = useState({});
 const [elapsedSeconds, setElapsedSeconds] = useState(0);
 const [loading, setLoading] = useState(true);
 const [error, setError] = useState("");

  useEffect(() => {
    if (userId && technology) {
      updateLearningConsistency(userId, 'MCQ_START', 'MCQ', technology, { level });
    }
  }, [userId, technology, level]);

 const location = useLocation();
 const [mode, setMode] = useState(location.state?.mode || "");
 const [showModeModal, setShowModeModal] = useState(!location.state?.mode);
 const [explanation, setExplanation] = useState("");
 const [explaining, setExplaining] = useState(false);
 const [showExplanation, setShowExplanation] = useState(false);
 const [explanationError, setExplanationError] = useState("");

 const userFromStorage = JSON.parse(localStorage.getItem("user"));
 
 // Get language preference from session or location state
 const { session } = useSkillGenieSession();
 const [mcqLanguage, setMcqLanguage] = useState("en");
 const language = location.state?.language || session?.language ||"en";

 const formatTime = (seconds) => {
 const mins = String(Math.floor(seconds / 60)).padStart(2,"0");
 const secs = String(seconds % 60).padStart(2,"0");
 return `00:${mins}:${secs}`;
 };

 const formatExplanationText = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (Array.isArray(value)) return value.filter(Boolean).join("\n");
  if (typeof value === "object") {
    return Object.entries(value)
      .map(([key, item]) => {
        if (typeof item === "string") return `${key}: ${item}`;
        if (Array.isArray(item)) return `${key}: ${item.join(", ")}`;
        if (item && typeof item === "object") {
          return `${key}:\n${Object.entries(item).map(([subKey, subItem]) => `- ${subKey}: ${subItem}`).join("\n")}`;
        }
        return "";
      })
      .filter(Boolean)
      .join("\n\n");
  }
  return String(value);
 };

 useEffect(() => {
 const timer = setInterval(() => setElapsedSeconds((prev) => prev + 1), 1000);
 return () => clearInterval(timer);
 }, []);

  const requiresPremium = parseInt(level) >= 6;
  const { canAccessFeature, showPremiumPopup } = useSubscription();
  const [hasAccess, setHasAccess] = useState(true);
  const [checkingAccess, setCheckingAccess] = useState(true);

  // Check access using centralized logic
  useEffect(() => {
    const allowed = canAccessFeature('TECH_EXPLORER_MCQ', { level: parseInt(level) });
    setHasAccess(allowed);
    setCheckingAccess(false);
    if (!allowed) {
        showPremiumPopup();
        navigate(-1);
    }
  }, [level, canAccessFeature, showPremiumPopup, navigate]);

  // Report initial attempt
  useEffect(() => {
    if (hasAccess && !checkingAccess && questions.length > 0) {
        axios.post(`${API_BASE_URL}/api/user/attempts`, {
            user_id: userId,
            technology,
            module: 'TechnologyMCQ',
            level: parseInt(level),
            score: 0,
            status: 'Started'
        }, { headers: { 'user-id': userId } }).catch(err => console.error('Failed to report attempt:', err));
    }
  }, [hasAccess, checkingAccess, questions.length, userId, technology, level]);

 useEffect(() => {
    if (userId && technology) {
      updateLearningConsistency(userId, 'MCQ_START', 'TechnologyMCQ', technology, { level });
    }
  }, [userId, technology, level]);

  useEffect(() => {
    let cancelled = false;

 const fetchQuestions = async () => {
 // Don't fetch if premium required and no access
 if (requiresPremium && !hasAccess) {
 if (!cancelled) {
 setLoading(false);
 }
 return;
 }

 try {
 const res = await axios.get(`${API_BASE_URL}/questions/technology/${technology}/level/${level}`);
 const data = typeof res.data.body ==="string" ? JSON.parse(res.data.body) : res.data;
 if (!cancelled) {
 if (Array.isArray(data) && data.length > 0) setQuestions(data);
 else setError("No questions found.");
 }
 } catch (err) {
 console.error("Error fetching questions:", err);
 if (!cancelled) setError("Failed to load questions.");
 } finally {
 if (!cancelled) setLoading(false);
 }
 };

 fetchQuestions();
 return () => { cancelled = true; };
 }, [technology, level, requiresPremium, hasAccess]);

  useEffect(() => {
    if (mode === "learning" && answers[currentIndex]) {
      fetchExplanation();
    }
  }, [currentIndex, mode, answers[currentIndex]]);

  const fetchExplanation = async () => {
    if (mode !== "learning") return;

    setExplaining(true);
    setExplanationError("");
    setExplanation(""); // Clear previous explanation
    setShowExplanation(false);

    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const userId = user?.userId || user?.user_id || localStorage.getItem("user_id");

      const res = await axios.post(`${API_BASE_URL}/api/explain`, {
        userId,
        explainType: 'mcq',
        context: {
          question: questions[currentIndex].question,
          options: {
            A: questions[currentIndex].option_a,
            B: questions[currentIndex].option_b,
            C: questions[currentIndex].option_c,
            D: questions[currentIndex].option_d,
          },
          correct_option: questions[currentIndex].correct_option?.toUpperCase(),
          language: mcqLanguage,
          technology,
          level
        }
      }, {
        headers: { 'user-id': userId }
      });

      if (res.data && res.data.ok) {
        setExplanation(formatExplanationText(res.data.explanation || res.data.data?.explanation) || "No explanation provided.");
        setShowExplanation(true);
      } else {
        setExplanationError(res.data?.error || "Failed to fetch explanation.");
      }
    } catch (err) {
      console.error("Error fetching explanation:", err);
      setExplanationError("Failed to generate explanation. Please try again.");
    } finally {
      setExplaining(false);
    }
  };

  const handleFinalSubmit = async () => {
    if (mode === "learning") {
      alert("Learning session completed! You can now return to the technology page.");
      navigate(-1);
      return;
    }

    const idBasedAnswers = {};
    questions.forEach((q, index) => {
      if (answers[index]) {
        idBasedAnswers[q.id] = answers[index];
      }
    });

    if (Object.keys(idBasedAnswers).length === 0) {
      alert("You have not answered any questions.");
      return;
    }

 try {
      const response = await axios.post(`${API_BASE_URL}/evaluate`, {
        answers: idBasedAnswers,
        technology,
        level,
        user_id: userId,
        time_taken: formatTime(elapsedSeconds),
        totalQuestions: questions.length,
      });

      const { correct, wrong, score } = response.data;

      if (userId) {
        axios.post(`${API_BASE_URL}/api/user/attempts`, {
          user_id: userId,
          technology,
          module: 'TechnologyMCQ',
          level: parseInt(level),
          score: score,
          status: 'Completed'
        }).catch(err => console.error('Failed to report final attempt:', err));

        updateLearningConsistency(userId, 'MCQ_SUBMIT', 'TechnologyMCQ', technology, { level, score: score });
      }

      if (score >= 80) {
        localStorage.setItem("levelCompleted","true");
        localStorage.setItem(
          "pendingLevelComplete",
          JSON.stringify({ technology, completedLevel: parseInt(level) })
        );
      }

      navigate("/levelclearance", {
        state: {
          correct,
          wrong,
          time: formatTime(elapsedSeconds),
          score: score,
          technology,
          level,
          answers: idBasedAnswers,
          user_id: userId,
          time_taken: elapsedSeconds,
          totalQuestions: questions.length, // Send total questions to LevelClearance
        },
      });
 } catch (err) {
 console.error("Submission error:", err);
 alert("Something went wrong while submitting your answers.");
 }
 };

 const handleOptionChange = (option) => {
 setAnswers((prev) => ({ ...prev, [currentIndex]: option }));
 };

 const handleQuestionChange = (newIndex) => {
 setCurrentIndex(newIndex);
 setShowExplanation(false);
 setExplanation("");
 };

 if (checkingAccess && requiresPremium) {
 return (
 <div className="min-h-screen bg-bgMain text-body flex items-center justify-center">
 <div className="text-center">
 <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
 <p>Verifying premium access...</p>
 </div>
 </div>
 );
 }

 if (requiresPremium && !hasAccess) {
 return (
 <div className="min-h-screen bg-bgMain text-body flex items-center justify-center px-4">
 <div className="text-center max-w-md">
 <div className="text-5xl mb-6">🔒</div>
 <h2 className="text-3xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent mb-4">Premium Access Required</h2>
 <p className="text-lg text-muted mb-8">
 Advanced and Expert Technology MCQs (Level 6+) require a SkillGenie Premium subscription.
 </p>
 <button
 onClick={() => navigate("/skillgenpayments")}
 className="btn-primary-gradient w-full text-xl py-4"
 >
 Upgrade to Premium
 </button>
 <button
 onClick={() => navigate(-1)}
 className="block mx-auto mt-6 text-muted hover:text-primary text-sm transition"
 >
 ← Go Back
 </button>
 </div>
 </div>
 );
 }

 if (loading) return <div className="bg-bgMain text-body min-h-screen p-6">Loading questions...</div>;
 if (error) return <div className="bg-bgMain text-red-500 min-h-screen p-6">{error}</div>;
 if (questions.length === 0) return <div className="bg-bgMain text-body min-h-screen p-6">No questions available.</div>;

 const currentQuestion = questions[currentIndex];
 const selectedOption = answers[currentIndex] ||"";
 const correctOption = currentQuestion.correct_option?.toUpperCase();
 const correctAnswerText = correctOption ? currentQuestion[`option_${correctOption.toLowerCase()}`] : "";

 return (
    <div className="flex bg-[#F8FAFC] text-body min-h-screen relative flex-row">
      {/* Sidebar - Fixed Royal Purple */}
      <div className="w-64 bg-[#5B21B6] p-5 flex flex-col h-screen text-white shadow-2xl z-[80] fixed left-0 top-0">
        <div className="mb-10 mt-4">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center shadow-lg bg-white/20">
              <SparklesIcon className="text-white h-6 w-6" />
            </div>
            <span className="text-xl font-black tracking-tight text-white uppercase">
              The Skill Genie
            </span>
          </div>

          <div className="text-[9px] font-black uppercase text-white/50 mb-1 tracking-widest">Technology</div>
          <div className="text-base font-bold text-white capitalize">{technology}</div>
          <div className="text-xs font-medium text-white/70">Level {level}</div>
        </div>
        
        <div className="mb-10">
          <div className="text-[9px] font-black uppercase text-white/50 mb-2 tracking-widest">Timer</div>
          <div className="text-3xl font-black text-white font-mono">
            {formatTime(elapsedSeconds)}
          </div>
        </div>

        <div className="text-[9px] font-black uppercase text-white/50 mb-3 tracking-widest">Questions</div>
        <div className="grid grid-cols-5 gap-2 text-xs overflow-y-auto custom-scrollbar pr-1 flex-1">
          {questions.map((_, idx) => (
            <button
              key={idx}
              onClick={() => handleQuestionChange(idx)}
              className={`w-9 h-9 rounded-xl font-bold transition-all ${
                idx === currentIndex
                  ? "bg-white text-[#5B21B6] shadow-xl scale-110"
                  : answers[idx]
                  ? "bg-white/30 text-white border border-white/20"
                  : "bg-white/5 text-white/40 border border-white/10 hover:border-white/30"
              }`}
            >
              {idx + 1}
            </button>
          ))}
        </div>

        <div className="mt-6 pt-6 border-t border-white/10">
          <button
            onClick={() => {
              navigate('/dashboard');
            }}
            className="w-full px-4 py-3 rounded-xl text-sm font-bold transition-all bg-white/10 hover:bg-white/20 text-white flex items-center justify-center gap-2"
          >
            <Home size={18} /> Exit to Home
          </button>
        </div>
      </div>

      {/* Main Panel */}
      <div className="flex-1 ml-64 p-8 flex flex-col items-center bg-[#F8FAFC] min-h-screen overflow-y-auto">
        {showModeModal && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[100]">
            <div className="bg-white p-10 rounded-[2.5rem] text-center shadow-2xl max-w-sm w-full animate-in zoom-in duration-300">
              <h2 className="text-2xl font-black text-gray-900 mb-8 uppercase tracking-tight">Select Mode</h2>
              <div className="flex flex-col gap-4">
                <button
                  onClick={() => {
                    setMode("learning");
                    setShowModeModal(false);
                  }}
                  className="w-full py-4 rounded-2xl bg-[#5B21B6] text-white font-bold hover:bg-[#4C1D95] transition-all shadow-lg shadow-purple-200 flex items-center justify-center gap-3"
                >
                  <GraduationCap size={20} /> Learning Mode
                </button>
                <button
                  onClick={() => {
                    setMode("practice");
                    setShowModeModal(false);
                  }}
                  className="w-full py-4 rounded-2xl bg-white border-2 border-[#5B21B6] text-[#5B21B6] font-bold hover:bg-purple-50 transition-all flex items-center justify-center gap-3"
                >
                  <Target size={20} /> Practice Mode
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="w-full max-w-4xl pt-4 pb-20">
            <div className="flex justify-between items-center mb-8">
               <button
                  onClick={() => navigate(`/practice/${technology}`)}
                  className="px-5 py-2 bg-primary text-white font-bold rounded-xl hover:bg-secondary transition shadow-lg shadow-primary/20 flex items-center gap-2 text-sm"
                >
                  <ArrowLeft size={16} /> Back to Skill Tree
                </button>
            </div>

            <div className="bg-white p-8 rounded-[2.5rem] shadow-xl border-2 border-primary/20 relative overflow-hidden mb-8">
              <div className="absolute top-0 left-0 w-1.5 h-full bg-primary"></div>
              
              <div className="mb-8">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-[9px] font-black uppercase text-primary tracking-[0.2em] bg-primary/5 px-3 py-1 rounded-full border border-primary/10">Level {level} • Question {currentIndex + 1} of {questions.length}</span>
                  <span className="text-[9px] font-black uppercase text-primary/60 tracking-widest">
                    {questions.length > 0 ? Math.round((currentIndex / questions.length) * 100) : 0}% Complete
                  </span>
                </div>
                
                {/* 📊 Progress Bar */}
                <div className="w-full h-1.5 bg-gray-100 rounded-full mb-6 overflow-hidden">
                  <div 
                    className="h-full bg-primary transition-all duration-500 ease-out"
                    style={{ width: `${questions.length > 0 ? (currentIndex / questions.length) * 100 : 0}%` }}
                  ></div>
                </div>

                <h2 className="text-2xl font-black text-midnight leading-tight">
                  {currentQuestion?.question}
                </h2>
              </div>

              <div className="space-y-3 mb-10">
                {["A", "B", "C", "D"].map((optKey) => {
                  const isSelected = selectedOption === optKey;
                  const isCorrect = currentQuestion.correct_option?.toUpperCase() === optKey;
                  const showResult = mode === "learning" && selectedOption;
                  
                  return (
                    <label
                      key={optKey}
                      className={`flex items-center gap-4 p-4 rounded-2xl cursor-pointer transition-all duration-300 border-2 ${
                        isSelected
                          ? showResult
                            ? isCorrect
                              ? "bg-green-50 border-green-500 shadow-lg shadow-green-100"
                              : "bg-red-50 border-red-500 shadow-lg shadow-red-100"
                            : "bg-primary/5 border-primary shadow-lg shadow-primary/10"
                          : showResult && isCorrect
                          ? "bg-green-50 border-green-500 shadow-lg shadow-green-100"
                          : "bg-gray-50 border-gray-100 hover:border-primary/30 hover:bg-white"
                      }`}
                    >
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-black transition-all text-sm ${
                        isSelected 
                          ? showResult
                            ? "bg-white " + (isCorrect ? "text-green-600" : "text-red-600")
                            : "bg-primary text-white" 
                          : showResult && isCorrect
                          ? "bg-white text-green-600"
                          : "bg-white text-gray-400 border border-gray-100"
                      }`}>
                        {optKey}
                      </div>
                      <input
                        type="radio"
                        name={`question-${currentIndex}`}
                        value={optKey}
                        checked={isSelected}
                        onChange={() => handleOptionChange(optKey)}
                        className="hidden"
                      />
                      <span className={`text-lg ${isSelected ? "text-midnight font-black" : "text-gray-700 font-bold"}`}>
                        {currentQuestion[`option_${optKey.toLowerCase()}`]}
                      </span>
                      {showResult && isSelected && (
                        <span className="ml-auto">
                          {isCorrect ? (
                            <span className="text-green-600 font-black text-sm uppercase">Correct!</span>
                          ) : (
                            <span className="text-red-600 font-black text-sm uppercase">Incorrect</span>
                          )}
                        </span>
                      )}
                    </label>
                  );
                })}
              </div>

              <div className="flex justify-between items-center pt-10 border-t border-gray-100">
                <button
                  className="text-gray-400 font-black uppercase tracking-widest text-xs hover:text-blue-600 disabled:opacity-30 disabled:cursor-not-allowed flex items-center gap-2 transition"
                  disabled={currentIndex === 0}
                  onClick={() => handleQuestionChange(currentIndex - 1)}
                >
                  ← Previous
                </button>

                <div className="flex items-center gap-4">
                  {mode === "learning" && (
                    <>
                      <select
                        value={mcqLanguage}
                        onChange={(e) => setMcqLanguage(e.target.value)}
                        className="bg-gray-50 text-gray-900 border border-gray-100 rounded-xl px-4 py-2.5 text-sm font-bold focus:outline-none focus:border-blue-600 transition-all"
                      >
                        {supportedLanguages.map((lang) => (
                          <option key={lang.code} value={lang.code}>
                            {lang.label}
                          </option>
                        ))}
                      </select>
                      <button
                        onClick={fetchExplanation}
                        className="px-8 py-3 bg-blue-50 text-blue-600 border border-blue-100 rounded-xl font-black uppercase tracking-widest text-xs hover:bg-blue-600 hover:text-white transition-all shadow-sm"
                        disabled={explaining}
                      >
                        {explaining ? "AI is thinking..." : "🧠 Re-explain"}
                      </button>
                    </>
                  )}

                  {currentIndex === questions.length - 1 ? (
                    <button
                      onClick={handleFinalSubmit}
                      className="px-10 py-4 rounded-2xl bg-blue-600 text-white font-black uppercase tracking-widest text-xs shadow-xl shadow-blue-500/20 hover:bg-blue-700 active:scale-95 transition-all"
                    >
                      Submit
                    </button>
                  ) : (
                    <button
                      onClick={() => handleQuestionChange(currentIndex + 1)}
                      className={`px-10 py-4 rounded-2xl font-black uppercase tracking-widest text-xs transition-all shadow-xl ${
                        answers[currentIndex]
                          ? "bg-blue-600 text-white hover:bg-blue-700 shadow-blue-500/20"
                          : "bg-gray-100 text-gray-400 cursor-not-allowed opacity-50"
                      }`}
                    >
                      Next Question →
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Explanation Area */}
            {mode === "learning" && selectedOption && (
              <div className="mt-8 bg-white p-6 rounded-[2rem] border-2 border-green-100 shadow-sm">
                <div className="text-[10px] font-black uppercase tracking-widest text-green-700 mb-2">Correct Answer</div>
                <div className="text-gray-900 font-black">
                  {correctOption}. {correctAnswerText}
                </div>
              </div>
            )}

            {explanationError && (
              <div className="mt-4 bg-red-50 p-4 rounded-2xl border border-red-100 text-red-700 font-bold text-sm">
                {explanationError}
              </div>
            )}

            {showExplanation && explanation && (
              <div className="mt-8 bg-blue-50/50 p-8 rounded-[2rem] border-2 border-blue-100 animate-in slide-in-from-bottom duration-500">
                <div className="flex items-center gap-3 mb-4">
                   <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center text-white">
                      <SparklesIcon className="w-6 h-6" />
                   </div>
                   <h3 className="font-black text-gray-900 uppercase tracking-widest text-xs">SkillGenie AI Explanation</h3>
                </div>
                <div className="prose prose-blue max-w-none text-gray-700 font-medium leading-relaxed whitespace-pre-line pl-2 border-l-4 border-blue-200">
                  {explanation}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
  );
};

export default QuestionPage;


